/**
 * GBI Quick Add — Alpine.js component for quick add-to-cart on product cards.
 *
 * Single-variant products add directly. Multi-variant products show a popover selector.
 * Respects minimum quantity and case pack size from product metafields.
 */
document.addEventListener('alpine:init', function () {
  Alpine.data('quickAdd', function (config) {
    return {
      variants: config.variants || [],
      selectedVariant: config.defaultVariant || null,
      minQty: config.minQty || 1,
      caseSize: config.caseSize || 1,
      hasMultipleVariants: config.hasMultipleVariants || false,
      loading: false,
      added: false,
      error: false,
      errorMessage: '',
      showVariants: false,
      _addedTimer: null,
      _errorTimer: null,

      get quantity() {
        return Math.max(this.minQty, this.caseSize);
      },

      _getCartStore() {
        try { return Alpine.store('cart') || null; } catch (e) { return null; }
      },

      async _ensureCartStore() {
        var store = this._getCartStore();
        if (store) return store;
        // Cart store not yet registered — poll briefly (up to 2s) for late initialization
        for (var i = 0; i < 40; i++) {
          await new Promise(function (r) { setTimeout(r, 50); });
          store = this._getCartStore();
          if (store) return store;
        }
        return null;
      },

      _showLocalError(msg) {
        this.error = true;
        this.errorMessage = msg || 'Something went wrong.';
        if (this._errorTimer) clearTimeout(this._errorTimer);
        var self = this;
        this._errorTimer = setTimeout(function () {
          self.error = false;
          self.errorMessage = '';
        }, 3000);
      },

      selectVariant(variantId) {
        this.selectedVariant = variantId;
        this.addToCart();
      },

      async addToCart() {
        if (this.loading || !this.selectedVariant) return;

        if (this.hasMultipleVariants && !this.showVariants) {
          this.showVariants = true;
          return;
        }

        this.loading = true;
        this.error = false;
        this.errorMessage = '';
        this.showVariants = false;

        var properties = {};
        var effectiveCaseSize = this.caseSize > 1 ? this.caseSize : this.minQty;
        if (effectiveCaseSize > 1) {
          properties._case_size = String(effectiveCaseSize);
        }
        properties._min_qty = String(this.minQty);

        var cart = await this._ensureCartStore();
        if (!cart) {
          this.loading = false;
          return;
        }

        var controller = new AbortController();
        var timeoutId = setTimeout(function () { controller.abort(); }, 10000);
        try {
          var res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: this.selectedVariant,
              quantity: this.quantity,
              properties: properties
            }),
            signal: controller.signal
          });
          clearTimeout(timeoutId);

          if (!res.ok) {
            var errorBody;
            try { errorBody = await res.json(); } catch (e) { /* non-JSON error response */ }
            var desc = errorBody && (typeof errorBody.description === 'string' ? errorBody.description : errorBody.message) || '';
            this.loading = false;
            this._showLocalError(desc || 'Something went wrong. Please try again.');
            return;
          }

          var body;
          try { body = await res.json(); } catch (e) {
            this.loading = false;
            this._showLocalError('Could not process response. Please try again.');
            return;
          }

          await cart.refresh();
          var title = body.product_title || body.title || 'Item';
          cart.announce('Added ' + title + ' to cart. ' + cart.itemCount + ' ' + (cart.itemCount === 1 ? 'item' : 'items') + ' in cart.');

          this.loading = false;
          this.added = true;
          if (this._addedTimer) clearTimeout(this._addedTimer);
          this._addedTimer = setTimeout(() => { this.added = false; }, 2000);
        } catch (e) {
          clearTimeout(timeoutId);
          this.loading = false;
          if (e.name === 'AbortError') {
            this._showLocalError('Request timed out. Please try again.');
          } else if (e.name === 'TypeError') {
            this._showLocalError('Check your connection and try again.');
          } else {
            this._showLocalError('Something went wrong. Please try again.');
          }
        }
      },

      destroy() {
        if (this._addedTimer) {
          clearTimeout(this._addedTimer);
          this._addedTimer = null;
        }
        if (this._errorTimer) {
          clearTimeout(this._errorTimer);
          this._errorTimer = null;
        }
      },

      closeVariants() {
        this.showVariants = false;
      }
    };
  });
});
